import { computed } from 'vue';
import { interpret } from 'xstate';
import { useActor } from '@xstate/vue';

import { CurrentKey, LoanType, RelationshipType } from '@/helpers/const';
import { formatAddress } from '@/helpers/format';
import { UserQuoteProcessStep, UserQuoteProcessStepNames, UserQuoteProcessSteps, StepStatus } from '@/types';

import { includesEnum } from './includes-enum';
import { initUserQuoteMachine, loanAppUserQuoteMachine, type UserQuoteMachineContext } from './loanUserQuoteMachine';
import { useFeatureFlags } from './useFeatureFlags';

import type { SectionStates, SectionStateType } from '@/types/SectionState';
import type {
  Address,
  Applicant,
  ApplicantId,
  AssetId,
  UserQuoteProcessStepEnum,
  UserQuoteProcessStepStatus,
  LoanApplication,
  PropertyId,
} from '@/types';
import type { UnaccreditedBrokerProfile } from '@/types/UnaccreditedBrokerProfile';

const appKey = localStorage.getItem(CurrentKey) ?? '';
const appItem = appKey ? localStorage.getItem(appKey) : null;
const appState = appItem ? JSON.parse(appItem) : null;

const initialState: UserQuoteMachineContext = {
  ...initUserQuoteMachine(),
  ...(appState ?? {}),
};

const service = interpret(loanAppUserQuoteMachine.withContext(initialState)).start();

service.onTransition((state) => {
  if (state.value !== 'gotoReady' && state.value !== 'start') {
    const currentStep = state.value as UserQuoteProcessStepEnum;
    if (state.context.status[currentStep] === StepStatus.Incomplete) {
      state.context.status[currentStep] = StepStatus.Ready;
    }

    state.context.current = currentStep;
    const loanApp = state.context.loanApp;

    if (loanApp.applicationId && loanApp.userType) {
      const key = `${loanApp.actionType}-${loanApp.applicationId}`;
      const appState = JSON.stringify(state.context);
      localStorage.setItem(CurrentKey, key);
      localStorage.setItem(key, appState);
    }
  }
});

const processSteps = Object.entries(UserQuoteProcessSteps).map(([step, info]) => ({
  id: step as UserQuoteProcessStepEnum,
  ...info,
}));

export function useUserQuoteProcess() {
  const { state, send } = useActor(service);
  const { isCommercialQuoteFlowEnabled } = useFeatureFlags();
  const authorisedUser = state.value.context.authorisedUser;

  const currentStep = computed(() => state.value.context.current);
  const loanApp = computed(() => state.value.context.loanApp);
  const startStateType = computed(() => state.value.context.startStateType);
  const sectionStates = computed(() => state.value.context.sectionStates);

  const secondaryApplicant = computed(() => {
    return loanApp.value.actionType === 'updateExisting'
      ? loanApp.value.applicants.find((a) => a.id === state.value.context.authorisedUser.id && a.type === 'secondary')
      : undefined;
  });

  const isSecondaryMarried = computed(
    () => secondaryApplicant.value?.relationshipToPrimary === RelationshipType.MARRIED_OR_DEFACTO,
  );

  const handleProcessSteps = computed(() => {
    if (loanApp.value.userType !== 'Crm') {
      return processSteps;
    }

    const incomingStep = processSteps.find((p) => p.id === UserQuoteProcessStep.IncomingProperties);
    const outgoingStep = processSteps.find((p) => p.id === UserQuoteProcessStep.OutgoingProperties);

    if (!incomingStep || !outgoingStep) {
      return processSteps;
    }

    return processSteps.map((step) => {
      if (step.id === UserQuoteProcessStep.IncomingProperties) return { ...outgoingStep, order: incomingStep.order };
      if (step.id === UserQuoteProcessStep.OutgoingProperties) return { ...incomingStep, order: outgoingStep.order };
      return step;
    });
  });

  const stepper = computed<UserQuoteProcessStepStatus[]>(() => {
    const currentStep = UserQuoteProcessStepNames.includes(state.value.context.current)
      ? state.value.context.current
      : UserQuoteProcessStepNames[0];

    const effectiveProcessSteps = Object.fromEntries(handleProcessSteps.value.map((p) => [p.id, p]));

    return handleProcessSteps.value
      .filter(
        (step) =>
          loanApp.value.userType &&
          step.actions.includes(loanApp.value.actionType) &&
          step.id !== UserQuoteProcessStep.Finished &&
          ((loanApp.value.endDebt > 0 && !isSecondaryMarried.value) ||
            state.value.context.from === 'crm' ||
            !includesEnum(step.id, [
              UserQuoteProcessStep.Income,
              UserQuoteProcessStep.Expenses,
              UserQuoteProcessStep.Assets,
            ]) ||
            ((loanApp.value.loanType === LoanType.SINGLE_SECURITY ||
              loanApp.value.loanType === LoanType.PURCHASE_ONLY) &&
              step.id === UserQuoteProcessStep.Assets)),
      )
      .map((step) => {
        const stepStatus = state.value.context.status[step.id];

        const status =
          stepStatus === StepStatus.Incomplete &&
          effectiveProcessSteps[step.id].order < effectiveProcessSteps[currentStep].order
            ? StepStatus.Skipped
            : stepStatus;
        if (isCommercialQuoteFlowEnabled() && step.id === UserQuoteProcessStep.IncomingProperties) {
          return { ...step, label: 'Property details', status };
        }

        return { ...step, status };
      })
      .filter((step) => {
        if (step.status === StepStatus.Skipped) {
          return false;
        }

        if (
          (isCommercialQuoteFlowEnabled() || loanApp.value.loanType === LoanType.PURCHASE_ONLY) &&
          step.id === UserQuoteProcessStep.OutgoingProperties
        ) {
          return false;
        }

        return true;
      });
  });

  const loanProperties = computed(() =>
    [...loanApp.value.incomingProperties, ...loanApp.value.outgoingProperties].reduce((obj, item) => {
      obj[item.id] = formatAddress(item.address as Address);
      return obj;
    }, {} as Record<PropertyId | AssetId, string>),
  );

  const loanApplicants = computed<Record<string, Applicant>>(() =>
    loanApp.value.applicants.reduce((obj, item) => {
      obj[item.id as string] = item;
      return obj;
    }, {} as Record<ApplicantId, Applicant>),
  );

  const saveBrokerAndGoNext = (unaccreditedBroker: UnaccreditedBrokerProfile) => {
    send({ type: 'NEXT_AND_SAVE_BROKER', unaccreditedBroker });
  };

  const saveAndGoNext = (
    updateData: Partial<LoanApplication> = {},
    options?: { nextStep?: UserQuoteProcessStepEnum },
  ) => {
    if (currentStep.value === UserQuoteProcessStep.SubmitUserQuote) {
      localStorage.setItem(CurrentKey, '');
    }

    const stepsWithStatus = handleProcessSteps.value.map((step) => ({
      ...step,
      status: state.value.context.status[step.id],
    }));
    send({
      type: 'NEXT',
      loanApp: { ...state.value.context.loanApp, ...updateData },
      processSteps: stepsWithStatus,
      ...(options?.nextStep !== undefined && { nextStep: options.nextStep }),
    });
  };

  const goBack = () => {
    send('BACK');
  };

  const restart = () => {
    send('RESTART');
  };

  const restartBroker = () => {
    send('RESTART_BROKER');
  };

  const updateStartStateType = (value: SectionStateType) => {
    send({ type: 'UPDATE_START_STATE_TYPE', value });
  };

  const updateSpecificSectionState = (sectionKey: keyof SectionStates, value: SectionStateType) => {
    send({ type: 'UPDATE_SECTION_STATE', sectionKey, value });
  };

  const updateAllSectionStates = (value: SectionStateType) => {
    send({ type: 'UPDATE_ALL_SECTION_STATES', value });
  };

  return {
    state,
    service,
    stepper,
    currentStep,
    authorisedUser,
    loanApp,
    loanProperties,
    loanApplicants,
    startStateType,
    sectionStates,
    send,
    saveAndGoNext,
    saveBrokerAndGoNext,
    goBack,
    restart,
    restartBroker,
    updateStartStateType,
    updateSpecificSectionState,
    updateAllSectionStates,
  };
}
